<template>
    <div v-if="loaded">
        <div class="p-8">
            <div class="md:w-10/12 m-auto">
                <div class="mb-4 flex items-center">
                    <div
                        class="flex flex-1 items-center justify-between shadow rounded-full px-6 py-2"
                    >
                        <input
                            type="search"
                            name="search"
                            placeholder="Search..."
                            class="w-full bg-white rounded-full text-sm focus:outline-none"
                            @input="search"
                            v-model="query"
                        />
                        <button type="submit" class="ml-2">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                class="text-gray-darker"
                            ></font-awesome-icon>
                        </button>
                    </div>
                    <div class="ml-2 shadow rounded-full px-4 py-2">
                        <button type="submit" @click.prevent="addNewIntent">
                            <font-awesome-icon
                                :icon="['fas', 'plus']"
                                class="text-gray-darker"
                            ></font-awesome-icon>
                        </button>
                    </div>
                </div>

                <div v-if="intents.length === 0">
                    <div class="p-8 text-center text-base">
                        <p><strong>📭 No history available.</strong></p>
                        <small class="text-gray-darker" v-if="this.query === ''"
                            >What's better than a blank slate to start creating
                            history?</small
                        >
                        <small class="text-gray-darker" v-else
                            >Nothing matches "{{ query }}".</small
                        >
                    </div>
                </div>
                <div v-else>
                    <div
                        class="hidden md:flex items-center uppercase text-xs font-bold text-gray-darker"
                    >
                        <div class="w-1/2 mx-2">
                            <p class="text-right">&larr; Allowed</p>
                        </div>
                        <div class="w-1/2 mx-2">
                            <p class="text-left">Blocked &rarr;</p>
                        </div>
                    </div>
                    <div
                        class="md:w-1/2 my-2 bg-primary-lightest p-2 rounded-full text-sm"
                        :class="intent.is_allowed ? 'mr-auto' : 'ml-auto'"
                        v-for="intent in intents"
                        :key="intent.id"
                    >
                        <div class="flex items-center">
                            <font-awesome-icon
                                :icon="['fas', getIcon(intent.type)]"
                                class="text-gray-darker text-xs mr-1"
                                :title="getTitle(intent.type)"
                            ></font-awesome-icon>
                            <div class="flex-1 min-w-0">
                                <p class="text-center truncate">
                                    {{ intent.value_human }}
                                </p>
                            </div>
                            <a
                                v-if="intent.type === 'query'"
                                @click.prevent="edit(intent)"
                                class="text-primary cursor-pointer mr-1"
                            >
                                <font-awesome-icon
                                    :icon="['fas', 'cog']"
                                    class="text-xs ml-1"
                                    title="Edit review"
                                />
                            </a>
                            <p-check
                                v-else
                                class="p-switch p-fill flex-shrink"
                                color="primary"
                                v-model="intent.is_allowed"
                                @change="toggle(intent.id, $event)"
                            ></p-check>
                            <a href @click.prevent="remove(intent.id)"
                                ><font-awesome-icon
                                    :icon="['fas', 'trash-alt']"
                                    class="text-destructive text-xs ml-1"
                                    title="Remove review"
                                ></font-awesome-icon
                            ></a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-else>
        <loading />
    </div>
</template>

<script>
import EditAutomationModal from "../automation/EditAutomationModal.vue";
import CreateNewAutomationModal from "./../automation/CreateNewAutomationModal.vue";
import RemoveReviewConfirmationModal from "./RemoveReviewConfirmationModal.vue";

export default {
    data() {
        return {
            loaded: false,
            query: "",
            intents: [],
            pagination: {},
        };
    },
    mounted() {
        this.onPage(1);
        this.handleScroll();
    },
    methods: {
        getTitle(type) {
            switch (type) {
                case "address":
                    return "Email Address";

                case "domain":
                    return "Domain";

                case "query":
                    return "Logic";

                default:
                    return "Keyword";
            }
        },
        getIcon(type) {
            switch (type) {
                case "address":
                    return "at";

                case "domain":
                    return "globe";

                case "query":
                    return "cog";

                default:
                    return "quote-left";
            }
        },
        handleScroll() {
            window.onscroll = () => {
                let bottomOfWindow =
                    document.documentElement.scrollTop + window.innerHeight >=
                    document.documentElement.offsetHeight;

                if (bottomOfWindow) this.getNextPage();
            };
        },
        getNextPage() {
            // if no more pages, exit early
            if (this.pagination.current >= this.pagination.total) return;

            axios
                .get(`/api/intents?page=${this.pagination.current + 1}`)
                .then((response) => {
                    this.intents = this.intents.concat(response.data.data);
                    this.setPagination(response);
                    this.loaded = true;
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        onPage(page) {
            axios
                .get(`/api/intents?page=${page}&query=${this.query}`)
                .then((response) => {
                    this.intents = response.data.data;
                    this.setPagination(response);
                    this.loaded = true;
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        setPagination(response) {
            this.pagination = {
                current: response.data.meta.current_page,
                total: response.data.meta.last_page,
                items_total: response.data.meta.total,
            };
        },
        search: _.debounce(function () {
            this.onPage(1);
        }, 300),
        toggle(id, isAllowed) {
            axios
                .put(`/api/intents/${id}/action`, {
                    action: isAllowed ? "allow" : "deny",
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        remove(id) {
            Modal.show(RemoveReviewConfirmationModal, {
                propsData: {
                    id: id,
                },
            })
                .then((response) => {
                    this.intents = this.intents.filter((intent) => {
                        return intent.id !== id;
                    });
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        addNewIntent() {
            Modal.show(CreateNewAutomationModal)
                .then(() => {
                    this.onPage(1);
                })
                .catch((err) => console.error);
        },
        async edit(intent) {
            await Modal.show(EditAutomationModal, {
                propsData: {
                    id: intent.id,
                },
            })
                .then(() => {
                    this.onPage(1);
                })
                .catch((err) => console.error);
        },
    },
};
</script>
